import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import applyTeamPlanRosterImport, {
  TeamPlanRosterImportCandidate,
} from './teamPlanRosterImport'

const AddActorRosterMenu = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { computed, timeline, updateTimeline } = useTeamPlanTimeline()

  const teamCandidates = useMemo(() => {
    return (team.members ?? [])
      .filter(
        (membership) =>
          membership.teamRank?.raid_visibility &&
          !membership.teamRank?.for_alts,
      )
      .map<TeamPlanRosterImportCandidate>((membership) => ({
        characterId: membership.characterReference.id,
        classId: membership.characterReference.classId ?? null,
        specId: membership.specId,
        role: membership.role,
        name: membership.characterReference.name,
      }))
  }, [team.members])

  const rosterFilters = useMemo(
    () => [
      {
        id: 'everyone',
        label: t(
          'dashboard.team.plans.assignments.entities.collection.everyone',
        ),
        role: null,
        candidates: teamCandidates,
      },
      {
        id: 'tanks',
        label: t('dashboard.team.plans.assignments.entities.collection.tanks'),
        role: 'tank' as const,
        candidates: teamCandidates.filter(
          (candidate) => candidate.role === 'tank',
        ),
      },
      {
        id: 'healers',
        label: t(
          'dashboard.team.plans.assignments.entities.collection.healers',
        ),
        role: 'heal' as const,
        candidates: teamCandidates.filter(
          (candidate) => candidate.role === 'heal',
        ),
      },
      {
        id: 'melee',
        label: t('dashboard.team.plans.assignments.entities.collection.melee'),
        role: 'melee' as const,
        candidates: teamCandidates.filter(
          (candidate) => candidate.role === 'melee',
        ),
      },
      {
        id: 'ranged',
        label: t('dashboard.team.plans.assignments.entities.collection.ranged'),
        role: 'ranged' as const,
        candidates: teamCandidates.filter(
          (candidate) => candidate.role === 'ranged',
        ),
      },
    ],
    [t, teamCandidates],
  )

  const previewByFilterId = useMemo(() => {
    return rosterFilters.reduce<
      Record<
        string,
        { addedCount: number; mergedCount: number; skippedCount: number }
      >
    >((acc, filter) => {
      const result = applyTeamPlanRosterImport({
        actors: timeline.actors,
        candidates: filter.candidates,
        specializations: computed.specializations,
      })
      acc[filter.id] = {
        addedCount: result.addedCount,
        mergedCount: result.mergedCount,
        skippedCount: result.skippedCount,
      }
      return acc
    }, {})
  }, [computed.specializations, rosterFilters, timeline.actors])

  const applyCandidates = (candidates: TeamPlanRosterImportCandidate[]) => {
    const result = applyTeamPlanRosterImport({
      actors: timeline.actors,
      candidates,
      specializations: computed.specializations,
    })
    if (result.addedCount + result.mergedCount === 0) return
    updateTimeline((next) => {
      next.actors = result.actors
    }, true)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size='sm' variant='outline'>
          {t('shared.roster')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {rosterFilters.map((filter) => {
          const preview = previewByFilterId[filter.id]
          const totalChanges =
            (preview?.addedCount ?? 0) + (preview?.mergedCount ?? 0)
          return (
            <DropdownMenuItem
              key={filter.id}
              disabled={totalChanges === 0}
              onClick={() => applyCandidates(filter.candidates)}
            >
              <div className='flex flex-col leading-tight'>
                <div className='flex items-center justify-between gap-2'>
                  <span>{filter.label}</span>
                  {filter.role ? (
                    <CharacterRoleIcon role={filter.role} size='small' />
                  ) : null}
                </div>
                <span className='text-xs text-muted-foreground'>
                  {t('dashboard.team.plans.roster.changes', {
                    added: preview?.addedCount ?? 0,
                    merged: preview?.mergedCount ?? 0,
                  })}
                </span>
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default AddActorRosterMenu
